'use client';

import { useState } from 'react';
import Link from '@mui/material/Link';
import { useTranslations } from 'next-intl';
import { FeedbackModal, type FeedbackFormData, useToast } from '@boty/ui';
import type { FeedbackButtonLabels } from '@boty/ui';

interface ConsumerFeedbackLinkProps {
  botId?: string | undefined;
}

/**
 * ConsumerFeedbackLink - Subtle feedback link for the consumer bot page
 * Story 16.4: Simplified feedback form (just description + optional email)
 */
export function ConsumerFeedbackLink({ botId }: ConsumerFeedbackLinkProps) {
  const t = useTranslations('common.feedbackForm');
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const labels: Required<FeedbackButtonLabels> = {
    tooltip: t('buttonTooltip'),
    ariaLabel: t('buttonAriaLabel'),
    title: t('title'),
    typeLabel: t('typeLabel'),
    types: {
      bug: t('types.bug'),
      feature_request: t('types.feature_request'),
      question: t('types.question'),
      other: t('types.other'),
    },
    descriptionLabel: t('descriptionLabel'),
    descriptionPlaceholder: t('descriptionPlaceholder'),
    descriptionMinLength: t('descriptionMinLength'),
    emailLabel: t('emailLabel'),
    emailPlaceholder: t('emailPlaceholder'),
    emailHelper: t('emailHelper'),
    screenshotLabel: t('screenshotLabel'),
    uploadScreenshot: t('uploadScreenshot'),
    removeScreenshot: t('removeScreenshot'),
    fileTooLarge: t('fileTooLarge'),
    submit: t('submit'),
    submitting: t('submitting'),
    success: t('success'),
    error: t('error'),
    cancel: t('cancel'),
  };

  async function handleSubmit(data: FeedbackFormData) {
    const payload = {
      description: data.description,
      email: data.email,
      url: window.location.href,
      botId,
    };

    const response = await fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const code = errorData?.error?.code;
      if (code === 'RATE_LIMITED') {
        toast.warning(t('rateLimited'));
      } else {
        toast.error(t('error'));
      }
      throw new Error(code || 'SUBMIT_FAILED');
    }

    toast.success(t('success'));
  }

  return (
    <>
      <Link
        component="button"
        onClick={() => setOpen(true)}
        sx={{
          color: 'text.primary',
          fontSize: 10,
          fontWeight: 700,
          opacity: 0.8,
          textDecoration: 'none',
          cursor: 'pointer',
          background: 'none',
          border: 'none',
          '@media (hover: hover)': {
            '&:hover': { opacity: 1 },
          },
        }}
      >
        {t('feedbackLink')}
      </Link>
      <FeedbackModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
        labels={labels}
        showScreenshot={false}
        showTypeSelector={false}
      />
    </>
  );
}
